import * as React from 'react';
import { ConnectDragPreview, ConnectDragSource, DragSource } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { oc } from 'ts-optchain';

import { Box } from '@tocktix/pantry';
import { DisplayTime } from '@tock/time';
import { DndItemTypes } from 'constants/dndItemTypes';
import { ServiceAerialTablePopoverPartyProps } from 'components/service/aerial/ServiceAerialTablePopoverPartyProps';
import { collectSource, partySource } from 'components/service/party/PartyDrag';
import { displayStatus, generateStateSelectorStatus, getButtonActions } from 'utils/partyUtils';
import PartyPanelSeatingBar from 'components/service/party-panel/PartyPanelSeatingBar';
import PartyPanelStateSelector from 'components/service/party-panel/PartyPanelStateSelector';
import ServicePartyCard from 'components/service/party/partyCardRefresh/ServicePartyCard';

interface DragConnectProps {
  connectDragPreview: ConnectDragPreview;
  connectDragSource: ConnectDragSource;
  isDragging: boolean;
}

const ServiceAerialTablePopoverPartyRefreshComponent: React.FC<
  ServiceAerialTablePopoverPartyProps & DragConnectProps
> = ({
  connectDragPreview,
  connectDragSource,
  dragMode,
  isReadOnly,
  onAssignTables,
  onFinishParty,
  onOpenParty,
  onPrintChit,
  onSeatParty,
  onTogglePin,
  onUpdatePartyState,
  party,
  partyPanelPosition,
  printOnSeat,
  secondaryStates,
}) => {
  React.useEffect(() => {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true,
    });
  }, [connectDragPreview]);

  const { isDisabled } = getButtonActions(party, secondaryStates, onSeatParty, onFinishParty);

  return (
    <Box className="ServiceAerialTable-popoverParty ServiceAerialTable-popoverParty--refresh">
      <ServicePartyCard
        bottomLeft={
          party.expectedReservationTime ? (
            <DisplayTime timeValue={party.expectedReservationTime} />
          ) : null
        }
        bottomRight={displayStatus(party)}
        onSelectParty={onOpenParty}
        party={party}
        topRight={party.partySize}
      />
      <PartyPanelSeatingBar
        connectDragSource={connectDragSource}
        isAssignable={!isReadOnly}
        isPartyStatusRefreshEnabled
        isReadOnly={isReadOnly}
        onToggleIsAssigningTables={onAssignTables}
        onTogglePinned={onTogglePin}
        party={party}
        visit={oc(party).visit()}
      />
      <Box className="PartyPanel-seatAndStatusRow">
        <PartyPanelStateSelector
          handleUpdatePartyState={onUpdatePartyState}
          isDisabled={isReadOnly || isDisabled}
          modifierClassName="PartyPanel-seatAndStatusRow-status"
          onPrintChit={onPrintChit}
          party={party}
          position={partyPanelPosition}
          printOnSeat={printOnSeat}
          secondaryStates={secondaryStates}
          status={generateStateSelectorStatus(party)}
        />
      </Box>
    </Box>
  );
};

export const ServiceAerialTablePopoverPartyRefresh = DragSource<
  ServiceAerialTablePopoverPartyProps,
  DragConnectProps
>(
  DndItemTypes.PARTY,
  {
    beginDrag(props: ServiceAerialTablePopoverPartyProps) {
      return partySource.beginDrag({
        dragSource: 'TABLE_MENU',
        onSuccessfulDrop: props.onSuccessfulDrop,
        party: props.party,
      });
    },
    canDrag(props: ServiceAerialTablePopoverPartyProps) {
      return partySource.canDrag({
        dragMode: props.dragMode,
        isReadOnly: props.isReadOnly,
        party: props.party,
      });
    },
  },
  collectSource
)(ServiceAerialTablePopoverPartyRefreshComponent);
